import { useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import UiContext from "../../../contexts/UiContext";
import ICart_Item from "../../../models/ICart_Item";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IKit_Builder_Data from "../../../models/IKit_Builder_Data";
import INewOrder_Item_Option from "../../../models/INewOrder_Item_Option";
import IUpdateCartItemDTO from "../../../models/DTO/IUpdateCartItemDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import CatalogItemService from "../../../services/CatalogItemService";
import { KitBuilderModal } from "../../order_entry/KitBuilderModal/KitBuilderModal";

type Props = {
    socket: Socket;
    /** The kit builder cart line being edited. */
    cartItem: ICart_Item;
    /** All cart lines (flat); the kit's children are picked out by main_kit_cart_item_id. */
    cartItems: ICart_Item[];
    /** Checkout sends this so tax/shipping are recalculated with the new kit price. */
    recalculateTotals?: boolean;
    /** Called when the update has been sent, so the host can show its busy state. */
    onSave?: () => void;
    onClose: () => void;
};

/**
 * Re-opens the KitBuilderModal for an existing kit builder cart line, pre-filled with the current
 * selections and item option values, and saves through CartUpdateItem. Mirrors the Order Entry
 * edit-kit flow: the cart line's Catalog_Item carries no kit options, so the kit is hydrated via
 * the catalog typeahead by guid first.
 */
export const EcommerceCartKitEditor = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);

    const [catalogItem, setCatalogItem] = useState<ICatalog_Item>();

    const notifyAndClose = (message: string, level: NotificationLevelEnum) => {
        uiContext.notifyFn(null, { message, level, position: NotificationPositionEnum.bc });
        props.onClose();
    };

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            const cartCatalogItem = props.cartItem.Catalog_Item;

            if (!vendorModel.Catalog?.guid || !cartCatalogItem) {
                notifyAndClose("unable_to_load_kit_for_editing", NotificationLevelEnum.error);
                return;
            }

            try {
                const result = await CatalogItemService.typeaheadCatalogItems(
                    vendorModel.Catalog.guid,
                    cartCatalogItem.sku || cartCatalogItem.name || "",
                    userModel,
                    supplierModel,
                    userModel.isSupplierUser ? undefined : vendorModel,
                    {
                        catalogItemGuid: cartCatalogItem.guid,
                        priceGroupGuid: vendorModel.Price_Group?.guid,
                        includeKitItems: true,
                        includeOptions: true,
                        limit: 100,
                    }
                );

                if (cancelled) {
                    return;
                }

                const hydrated = result.catalog_items?.find((ci) => ci.id === props.cartItem.catalog_item_id);

                if (!hydrated?.Kit_Builder_Options?.length) {
                    notifyAndClose("unable_to_load_kit_for_editing", NotificationLevelEnum.error);
                    return;
                }

                // A sub-option choice is not persisted on the cart line, so editing would lose it.
                if (CatalogItemService.kitBuilderHasSubOptions(hydrated)) {
                    notifyAndClose("editing_kits_with_sub_options_not_supported", NotificationLevelEnum.warning);
                    return;
                }

                setCatalogItem(hydrated);
            } catch {
                if (!cancelled) {
                    notifyAndClose("unable_to_load_kit_for_editing", NotificationLevelEnum.error);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [props.cartItem.guid]);

    const save = (kitBuilderData: IKit_Builder_Data, itemOptions?: Partial<INewOrder_Item_Option>[]) => {
        const payload: IUpdateCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            cartItemGuid: props.cartItem.guid,
            kitBuilderData,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        const itemOptionValues = (itemOptions || [])
            .filter((option) => option.item_option_id !== undefined)
            .map((option) => ({ item_option_id: option.item_option_id!, value: option.value ?? "" }));

        if (itemOptionValues.length) {
            payload.itemOptionValues = itemOptionValues;
        }

        if (props.recalculateTotals) {
            payload.recalculateTotals = true;
        }

        props.socket.emit(EndpointsEnum.CartUpdateItem, payload);
        props.onSave?.();
    };

    if (!catalogItem) {
        return null;
    }

    return (
        <KitBuilderModal
            key={props.cartItem.guid}
            catalogItem={catalogItem}
            initialKitBuilderData={CatalogItemService.buildKitBuilderDataFromCartItems(
                props.cartItem,
                props.cartItems,
                catalogItem.Kit_Builder_Options || []
            )}
            initialItemOptions={(props.cartItem.Cart_Item_Options || []).map((option) => ({
                item_option_id: option.item_option_id,
                value: option.value,
            }))}
            showItemOptions={true}
            addToCartOrOrder={save}
            disableSelection={false}
            saveEndpointReturn={EndpointsEnum.CartOnUpdateItem}
            autoOpenOnMount={true}
            hideTriggerButton={true}
            editMode={true}
            onClose={props.onClose}
        />
    );
};
